import { useMemo } from 'react';
import {
  type BffClient,
  type CanvasRuntimeContext,
  type ESAStoreContract,
  type PermissionSet,
  type PlatformTheme,
  type SurfaceContext,
  type SurfaceContract,
  type SurfaceType,
  themeForMode,
} from '@growthaxl/web-foundation';
import { getBffAdapter } from '../adapters';
import { useShellState, useShellStore } from './store/shellStore';
import { useShellEvent } from './events/useShellEvent';
import { useESABus } from '../features/Esa/esaBus';
import { toPermissionSet } from './permissions';
import { SurfaceBffClient } from './SurfaceBffClient';
import { createCanvasAwarenessPublisher } from '../features/Canvas/hooks/useCanvasAwarenessImpl';
import { USER_THEME_STORAGE_KEY } from '../constants/storageKeys';

/** Read persisted theme from localStorage. */
function getPersistedTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem(USER_THEME_STORAGE_KEY);
  return stored === 'dark' ? 'dark' : 'light';
}

/** §7.2 useTheme() — resolved from personalization (User -> Tenant -> Platform). */
export function useTheme(): PlatformTheme {
  const storeTheme = useShellStore(state => state.personalization?.theme);
  const mode = storeTheme ?? getPersistedTheme();
  return useMemo(() => themeForMode(mode), [mode]);
}

/** §7.2 usePermissions() — render-time permission set from the login manifest. */
export function usePermissions(): PermissionSet {
  const manifest = useShellStore(state => state.permissions);
  return useMemo(() => toPermissionSet(manifest), [manifest]);
}

const noopESAStore: ESAStoreContract = {
  setContext: () => undefined,
  jumpToThread: () => undefined,
  startResolution: () => undefined,
  clearPendingOpenRun: () => undefined,
};

/** Convenience factory binding a Surface's hooks to its id (used by remotes). */
export function createSurfaceContract(surfaceId: string, surfaceType: SurfaceType = 'workspace'): SurfaceContract {
  const surfaceContext: SurfaceContext = {
    surfaceId,
    surfaceType,
    route: '',
    params: {},
  };
  let workspaceBffClient: SurfaceBffClient | null = null;

  return {
    useShellState,
    useShellEvent,
    useESABus: () => useESABus(surfaceId, surfaceType),
    useESAStore: () => noopESAStore,
    useTheme,
    usePermissions,
    onMount: () => undefined,
    onUnmount: () => undefined,
    onVisibilityChange: () => undefined,
    // Canvas awareness publisher — backed by the host Zustand store with
    // per-ring debounce. The same publisher instance is reused across calls
    // (createCanvasAwarenessPublisher is stable — it holds debounce state).
    useCanvasAwareness: createCanvasAwarenessPublisher,
    createBffClient: (canvasContext?: CanvasRuntimeContext | null): BffClient => {
      if (!canvasContext) {
        if (!workspaceBffClient) {
          workspaceBffClient = new SurfaceBffClient(
            getBffAdapter(),
            surfaceContext,
            null,
          );
        }
        return workspaceBffClient;
      }

      return new SurfaceBffClient(getBffAdapter(), surfaceContext, canvasContext);
    },
  };
}

export { useShellState, useShellEvent, useESABus };
